<template>
  <div
    :class="{'chips-select': useChips}"
    class="app-select"
  >
    <div
      v-if="label"
      class="text-14-400 text-dark flex items-center label-input"
      :style="labelStyle"
    >
      {{ label }}
    </div>
    <div
      v-if="desc"
      class="text-dark-2 q-pb-sm text-14-400"
    >
      {{ desc }}
    </div>
    <q-select
      ref="select"
      :dropdown-icon="dropdownIcon"
      :hint="hint"
      :rules="rules"
      :use-chips="useChips"
      :options="localOptions"
      :value="value"
      :input-debounce="inputDebounce"
      :option-label="optionLabel"
      :loading="loading"
      class="text-14-400"
      label-slot
      stack-label
      no-error-icon
      outlined
      hide-bottom-space
      v-bind="$attrs"
      @blur="$emit('blur', $event)"
      @clear="$emit('clear', $event)"
      @click="$emit('click', $event)"
      @focus="$emit('focus', $event)"
      @input="$emit('input', $event)"
      @popup-hide="$emit('popup-hide', $event)"
      @popup-show="$emit('popup-show', $event)"
      @filter="filterHandler"
    >
      <template
        v-if="!value"
        #label
      >
        {{ placeholder }}
      </template>
      <template #append>
        <slot name="append"/>
      </template>
      <template #prepend>
        <slot name="prepend"/>
      </template>
      <template
        v-if="loading && !$slots.selected"
        #selected
      >
        <!-- Hide select value while options loading -->
        <slot name="selected"/>
      </template>

      <template
        v-for="(_, slot) of $scopedSlots"
        v-slot:[slot]="scope"
      >
        <slot
          v-if="$scopedSlots[slot]"
          :name="slot"
          v-bind="scope"
        />
      </template>

      <template
        v-if="useChips && !$scopedSlots['selected-item']"
        v-slot:selected-item="scope"
      >
        <q-chip
          :tabindex="scope.tabindex"
          class="q-ma-none"
          dense
          icon-remove="close"
          removable
          @remove="scope.removeAtIndex(scope.index)"
        >
          {{ scope.opt[optionLabel] }}
        </q-chip>
      </template>
      <template
        v-if="!$scopedSlots['no-option']"
        v-slot:no-option
      >
        <q-item>
          <q-item-section class="text-grey">
            {{ $t('label.noResult') }}
          </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
</template>

<script>
export default {
  name: 'AppSelect',
  props: {
    value: {
      type: [Object, Array, String, Number],
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    desc: {
      type: String,
      default: undefined,
    },
    options: {
      type: Array,
      default: () => [],
    },
    dropdownIcon: {
      type: String,
      default: 'expand_more',
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    optionLabel: {
      type: String,
      default: undefined,
    },
    hint: {
      type: String,
      default: undefined,
    },
    rules: {
      type: Array,
      default: undefined,
    },
    useChips: {
      type: Boolean,
      default: false,
    },
    useFilter: {
      type: Boolean,
      default: false,
    },
    useSearch: {
      type: Boolean,
      default: false,
    },
    inputDebounce: {
      type: Number,
      default: 0,
    },
    designOptions: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localOptions: this.options,
    };
  },
  computed: {
    labelStyle() {
      if (this.designOptions) {
        return {
          fontSize: `${this.designOptions.labelFontSize}px !important`,
          color: `${this.designOptions.labelColor} !important`,
        };
      }
      return undefined;
    },
  },
  watch: {
    options(value) {
      this.localOptions = value;
    },
  },
  methods: {
    add(option, isUnique = false) {
      this.$refs.select.add(option, isUnique);
    },
    filterHandler(value, update) {
      if (this.useSearch) {
        if (value === '') {
          update(() => {
            this.localOptions = this.options;
          });
          return;
        }

        update(() => {
          const needle = value.toLowerCase();
          this.localOptions = this.options.filter((item) => item.label.toLowerCase()
            .includes(needle));
        });
      } else if (this.useFilter) {
        this.$emit('filter', {
          value,
          update,
        });
      } else {
        update();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: 1024px) {
  .app-select {
    ::v-deep .q-field {
      &--outlined {
        &.q-field--highlighted {
          .q-field__control {
            &:after {
              border-color: currentColor;
              border-width: 1px;
            }
          }
        }

        .q-field__control {
          border-radius: $radius-10;
          padding: 0 18px;
          height: auto !important;
          min-height: 50px !important;

          &:after {
            border-width: 1px;
          }
        }
      }
    }
  }
  ::v-deep .q-chip {
    &--dense {
      border-radius: $radius-6 !important;
      padding: 18px 12px 18px 14px !important;
      margin: 2px !important;
    }
  }
}

.app-select {
  .label-input {
    height: 50px;
  }
  ::v-deep .q-field {
    &__label {
      color: $grey !important;
      font-size: 14px;
      margin-top: -2px;
    }

    &--float .q-field__label {
      transform: none !important;
      right: inherit !important;
      transition: none !important;
    }

    &__control-container {
      padding-top: 8px;
    }

    &__marginal {
      color: $primary;
    }

    &__bottom {
      padding: 8px 0;
    }

    &--auto-height .q-field__control {
      min-height: 50px;
    }

    &__before, &__prepend {
      padding-right: 0;
    }

    &--error {
      .q-icon {
        color: $negative !important;
      }
    }

    &__messages {
      color: currentColor !important;
    }

    &__marginal {
      height: auto;
    }

    &--outlined {
      &.q-field--highlighted {
        .q-field__control {
          &:before {
            border-color: currentColor;
          }
          &:after {
            border-color: currentColor;
            border-width: 1px;
          }
        }
      }

      .q-field__control {
        border-radius: $radius-6;
        padding: 0 16px;
        height: auto !important;
        &:before {
          border-color: $grey-2;
        }
        &:after {
          border-width: 1px;
        }
      }
    }
  }

  ::v-deep .q-placeholder {
    &::placeholder {
      color: $grey !important;
      opacity: 1 !important;
    }
  }

  ::v-deep .q-chip {
    color: white;
    background-color: $primary;
    font-size: 16px;
    margin: 0 6px 0 0;

    &--dense {
      border-radius: $radius-6 !important;
      padding: 18px 12px 18px 14px !important;
      margin: 2px !important;
    }

    &__icon {
      color: white;
      opacity: 1;
      margin-left: 8px;
      font-size: 16px;
    }
  }
}

.chips-select {
  ::v-deep .q-field {
    &__control {
      padding: 0 14px 0 7px !important;
    }
  }
}
</style>
